import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '@/App';
import { ArrowLeft, Bug, Trophy, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const SmartContractDebugger = () => {
  const navigate = useNavigate();
  const { fetchProfile } = useAuth();
  const [gameStarted, setGameStarted] = useState(false);
  const [currentBug, setCurrentBug] = useState(0);
  const [selectedLine, setSelectedLine] = useState(null);
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);

  const buggyContracts = [
    {
      title: 'Reentrancy Vulnerability',
      code: [
        'function withdraw(uint amount) public {',
        '  require(balances[msg.sender] >= amount);',
        '  msg.sender.call.value(amount)("");', // Bug: reentrancy
        '  balances[msg.sender] -= amount;',
        '}'
      ],
      bugLine: 2,
      explanation: 'The external call should happen AFTER updating the balance to prevent reentrancy attacks.'
    },
    {
      title: 'Integer Overflow',
      code: [
        'function transfer(address to, uint amount) public {',
        '  balances[msg.sender] -= amount;',
        '  balances[to] += amount;', // Bug: no overflow check
        '  emit Transfer(msg.sender, to, amount);',
        '}'
      ],
      bugLine: 2,
      explanation: 'Should use SafeMath or Solidity 0.8+ to prevent integer overflow/underflow.'
    },
    {
      title: 'Missing Access Control',
      code: [
        'function setOwner(address newOwner) public {', // Bug: no access control
        '  owner = newOwner;',
        '  emit OwnerChanged(owner, newOwner);',
        '}'
      ],
      bugLine: 0,
      explanation: 'This function should have an onlyOwner modifier to prevent unauthorized access.'
    }
  ];

  const handleLineClick = (lineIndex) => {
    const contract = buggyContracts[currentBug];
    if (lineIndex === contract.bugLine) {
      setScore(score + 400);
      toast.success(`Bug found! +400 points`);
      setTimeout(() => {
        if (currentBug < buggyContracts.length - 1) {
          setCurrentBug(currentBug + 1);
          setSelectedLine(null);
        } else {
          endGame();
        }
      }, 3000);
    } else {
      toast.error('Not the buggy line. Keep looking!');
    }
    setSelectedLine(lineIndex);
  };

  const endGame = async () => {
    setGameOver(true);
    try {
      const response = await axios.post(`${API}/games/submit`, {
        game_type: 'debugger',
        score: score,
        time_taken: 0
      });
      toast.success(`Debugging complete! Earned ${response.data.xp_gained} XP`);
      await fetchProfile();
    } catch (error) {
      console.error('Failed to submit score', error);
    }
  };

  if (!gameStarted) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-start-screen">
            <Bug className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">Smart Contract Debugger</h1>
            <p className="text-zinc-400 mb-6 leading-relaxed">
              Find security vulnerabilities in smart contracts. Click on the line containing the bug. Each bug found earns 400 points!
            </p>
            <Button
              onClick={() => setGameStarted(true)}
              data-testid="start-game-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 hover:shadow-green-glow uppercase tracking-wide px-8 py-6 text-base h-auto"
            >
              Start Debugging
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="mt-4 text-zinc-400 hover:text-primary"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="max-w-md w-full mx-auto px-6">
          <div className="glass-panel rounded-xl p-8 text-center" data-testid="game-over-screen">
            <Trophy className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="text-3xl font-grotesk font-bold text-white mb-4">All Bugs Fixed!</h1>
            <div className="mb-6">
              <div className="text-5xl font-mono font-bold text-primary mb-2" data-testid="final-score">{score}</div>
              <div className="text-zinc-400">Total Score</div>
            </div>
            <Button
              onClick={() => window.location.reload()}
              data-testid="play-again-button"
              className="bg-primary text-black font-bold hover:bg-primary/90 uppercase tracking-wide px-8 py-3 mb-3 w-full"
            >
              Debug Again
            </Button>
            <Button
              variant="ghost"
              onClick={() => navigate('/games')}
              className="text-zinc-400 hover:text-primary w-full"
            >
              Back to Games
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const contract = buggyContracts[currentBug];

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-6 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/games')}
          data-testid="back-to-games-button"
          className="mb-4 text-zinc-400 hover:text-primary"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Exit Debugger
        </Button>

        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-grotesk font-bold text-white mb-1">{contract.title}</h1>
            <p className="text-zinc-400 text-sm">Contract {currentBug + 1} of {buggyContracts.length}</p>
          </div>
          <div className="text-2xl font-mono font-bold text-primary" data-testid="game-score">{score}</div>
        </div>

        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-xs font-mono uppercase tracking-wider text-primary/80">Smart Contract Code</span>
            <Bug className="w-5 h-5 text-red-500" />
          </div>
          <div className="bg-black/50 rounded-lg p-4 font-mono text-sm">
            {contract.code.map((line, index) => {
              const isBugLine = selectedLine !== null && index === contract.bugLine;
              const isSelected = selectedLine === index;
              return (
                <div
                  key={index}
                  onClick={() => handleLineClick(index)}
                  data-testid={`code-line-${index}`}
                  className={`py-2 px-3 rounded cursor-pointer transition-all hover:bg-zinc-800/50 ${
                    isBugLine
                      ? 'bg-primary/20 border-l-4 border-primary'
                      : isSelected
                      ? 'bg-red-500/20 border-l-4 border-red-500'
                      : ''
                  }`}
                >
                  <span className="text-zinc-500 mr-4">{index + 1}</span>
                  <span className="text-zinc-300">{line}</span>
                  {isBugLine && <CheckCircle className="inline-block ml-2 w-4 h-4 text-primary" />}
                </div>
              );
            })}
          </div>
        </div>

        {selectedLine === contract.bugLine && (
          <div className="bg-primary/10 border border-primary/30 rounded-xl p-6">
            <h3 className="text-sm font-mono uppercase tracking-wider text-primary mb-2">Explanation</h3>
            <p className="text-zinc-300 leading-relaxed">{contract.explanation}</p>
          </div>
        )}

        <div className="bg-zinc-900/30 border border-white/10 rounded-xl p-6">
          <h3 className="text-sm font-mono uppercase tracking-wider text-primary/80 mb-3">Instructions</h3>
          <p className="text-zinc-400 text-sm">Click on the line of code that contains a security vulnerability or bug.</p>
        </div>
      </div>
    </div>
  );
};

export default SmartContractDebugger;
